import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import WirePartsPage from './pages/wire/WirePartsPage';
import WireCostingsPage from './pages/wire/WireCostingsPage';
import WireQuotationsPage from './pages/wire/WireQuotationsPage';
import WireQuotationDetailPage from './pages/wire/WireQuotationDetailPage';
import { WireQuotationPrintView } from './pages/print-views/WireQuotationPrintView';
import MachinesPage from './pages/MachinesPage';
import PartsPage, { PartDetailPage } from './pages/PartsPage';
import InquiryRegisterPage from './pages/inquiries/InquiryRegisterPage';
import InquiryCreatePage from './pages/inquiries/InquiryCreatePage';
import InquiryWorkspacePage from './pages/inquiries/InquiryWorkspacePage';
import VendorMasterPage from './pages/vendors/VendorMasterPage';
import VendorRfqPrintView from './pages/print-views/VendorRfqPrintView';
import BomKitCostSheetPrintView from './pages/print-views/BomKitCostSheetPrintView';
import OrderReviewSheetPrintView from './pages/print-views/OrderReviewSheetPrintView';
import OrderReviewSheetPage from './pages/orders/OrderReviewSheetPage';
import ReviewWorklistPage from './pages/orders/ReviewWorklistPage';
import DuplicateCustomersPage from './pages/DuplicateCustomersPage';
import MyFollowupsPage from './pages/MyFollowupsPage';
import ProcessFlowsPage, { ProcessFlowBuilderPage, BOMBuilderPage } from './pages/ProcessFlowsPage';
import CostingsPage from './pages/CostingsPage';
import { NewCostingPage } from './pages/NewCostingPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SettingsPage from './pages/SettingsPage';
import { CostingDetailPage } from './pages/CostingDetailPage';
import QuotationsPage, { NewQuotationPage } from './pages/QuotationsPage';
import { QuotationDetailPage } from './pages/QuotationDetailPage';
import OrdersPage from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import ProductionPlanningPage from './pages/ProductionPlanningPage';
import DocumentProcessingPage from './pages/DocumentProcessingPage';
import UsersPage, { AuditLogsPage, AdminExportsPage, DropdownManagerPage, EmailSettingsPage, DataBackupsPage, MaterialMasterPage, EmailStagingInboxPage, ActivityLogsPage, UserAnalyticsPage, RecycleBinPage } from './pages/AdminPages';
import WasherCalculatorPage from './pages/WasherCalculatorPage';
import { QuotationPrintView, ProcessFlowPrintView, CostingPrintView, OrderPrintView, WorkOrderPrintView, PlanningSheetPrintView, OrderProductionReportPrintView, RMRequirementsReportPrintView } from './pages/PrintViews';
import './App.css';

// Access Denied component for Label Operators trying to access other modules
function AccessDeniedPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-primary mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-6">You don't have permission to access this page.</p>
        <a href="/dashboard" className="text-primary hover:underline">
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children, requiredRoles, allowOnForceChange = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin force-reset gate: until the user clears `force_password_change`,
  // every page redirects to /change-password. The change-password route itself
  // opts out via `allowOnForceChange`.
  if (user.force_password_change && !allowOnForceChange) {
    return <Navigate to="/change-password" replace state={{ from: location }} />;
  }

  if (requiredRoles && !requiredRoles.includes(user.role)) {
    return <AccessDeniedPage />;
  }

  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
}

// Public Route Component (redirects if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* AI Document Processing */}
      <Route path="/document-processing" element={<ProtectedRoute><DocumentProcessingPage /></ProtectedRoute>} />
      
      {/* Master Data */}
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetailPage /></ProtectedRoute>} />
      <Route path="/wire/parts" element={<ProtectedRoute><WirePartsPage /></ProtectedRoute>} />
      <Route path="/wire/costings" element={<ProtectedRoute><WireCostingsPage /></ProtectedRoute>} />
      <Route path="/wire/quotations" element={<ProtectedRoute><WireQuotationsPage /></ProtectedRoute>} />
      <Route path="/wire/quotations/:id" element={<ProtectedRoute><WireQuotationDetailPage /></ProtectedRoute>} />
      <Route path="/wire/quotations/:id/print" element={<ProtectedRoute><WireQuotationPrintView /></ProtectedRoute>} />
      <Route path="/machines" element={<ProtectedRoute><MachinesPage /></ProtectedRoute>} />
      
      {/* Parts Master */}
      <Route path="/parts" element={<ProtectedRoute><PartsPage /></ProtectedRoute>} />
      <Route path="/parts/:id" element={<ProtectedRoute><PartDetailPage /></ProtectedRoute>} />
      
      {/* Inquiries */}
      <Route path="/inquiries" element={<ProtectedRoute><InquiryRegisterPage /></ProtectedRoute>} />
      <Route path="/inquiries/new" element={<ProtectedRoute><InquiryCreatePage /></ProtectedRoute>} />
      <Route path="/inquiries/:id" element={<ProtectedRoute><InquiryWorkspacePage /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><VendorMasterPage /></ProtectedRoute>} />
      
      {/* Process Flows */}
      <Route path="/process-flows" element={<ProtectedRoute><ProcessFlowsPage /></ProtectedRoute>} />
      <Route path="/process-flows/new" element={<ProtectedRoute><ProcessFlowBuilderPage /></ProtectedRoute>} />
      <Route path="/process-flows/:id" element={<ProtectedRoute><ProcessFlowBuilderPage /></ProtectedRoute>} />
      
      {/* BOM (Bill of Materials) for Kits */}
      <Route path="/bom/new" element={<ProtectedRoute><BOMBuilderPage /></ProtectedRoute>} />
      <Route path="/bom/:id" element={<ProtectedRoute><BOMBuilderPage /></ProtectedRoute>} />
      
      {/* Costings */}
      <Route path="/costings" element={<ProtectedRoute><CostingsPage /></ProtectedRoute>} />
      <Route path="/costings/new" element={<ProtectedRoute><NewCostingPage /></ProtectedRoute>} />
      <Route path="/costings/:id" element={<ProtectedRoute><CostingDetailPage /></ProtectedRoute>} />
      
      {/* Quotations */}
      <Route path="/quotations" element={<ProtectedRoute><QuotationsPage /></ProtectedRoute>} />
      <Route path="/quotations/new" element={<ProtectedRoute><NewQuotationPage /></ProtectedRoute>} />
      <Route path="/quotations/:id" element={<ProtectedRoute><QuotationDetailPage /></ProtectedRoute>} />
      
      {/* Sales Orders */}
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/orders/:orderId/review-sheet" element={<ProtectedRoute><OrderReviewSheetPage /></ProtectedRoute>} />
      <Route path="/order-reviews" element={<ProtectedRoute><ReviewWorklistPage /></ProtectedRoute>} />
      <Route path="/customers/duplicates" element={<ProtectedRoute><DuplicateCustomersPage /></ProtectedRoute>} />
      <Route path="/followups" element={<ProtectedRoute><MyFollowupsPage /></ProtectedRoute>} />
      
      {/* Production Planning */}
      <Route path="/production" element={<ProtectedRoute><ProductionPlanningPage /></ProtectedRoute>} />
      
      {/* Engineering Tools */}
      <Route path="/engineering/washer-calculator" element={<ProtectedRoute><WasherCalculatorPage /></ProtectedRoute>} />

      {/* Account: change own password (allowed even when force_password_change=true) */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute allowOnForceChange={true}>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Admin settings landing */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin: Material Master */}
      <Route 
        path="/admin/material-master" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'manager']}>
            <MaterialMasterPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <UsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit-logs" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <AuditLogsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/exports" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <AdminExportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/master-data" 
        element={
          <ProtectedRoute requiredRoles={['admin', 'manager']}>
            <DropdownManagerPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/email-settings" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <EmailSettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/email-inbox" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <EmailStagingInboxPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/data-backups" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DataBackupsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/activity-logs" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <ActivityLogsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/user-analytics" 
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <UserAnalyticsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/admin/recycle-bin"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <RecycleBinPage />
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      
      {/* Print Views - No Layout wrapper */}
      <Route path="/quotations/:id/print" element={<QuotationPrintView />} />
      <Route path="/process-flows/:id/print" element={<ProcessFlowPrintView />} />
      <Route path="/costings/:id/print" element={<CostingPrintView />} />
      <Route path="/orders/:id/print" element={<OrderPrintView />} />
      <Route path="/orders/:id/production-report" element={<OrderProductionReportPrintView />} />
      <Route path="/work-orders/:woId/print" element={<WorkOrderPrintView />} />
      <Route path="/planning/print" element={<PlanningSheetPrintView />} />
      <Route path="/production/rm-report" element={<RMRequirementsReportPrintView />} />
      <Route path="/vendor-rfqs/:id/print" element={<VendorRfqPrintView />} />
      <Route path="/bom/:id/print" element={<BomKitCostSheetPrintView />} />
      <Route path="/order-reviews/:id/print" element={<OrderReviewSheetPrintView />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          theme="dark"
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
